import { clamp } from 'lodash';
import { useState, useMemo, useCallback } from 'react';
import { usePrevious } from '@client/hooks/use-utils.hooks';

type PageNavigationProps<T> = {
  pages: T[];
};
export type PageNavigation<T> = {
  /**
   * The current page index
   */
  currentIndex: number;
  /**
   * The current page
   */
  current: T | undefined;
  /**
   * Go to the next page
   */
  next: (() => void) | undefined;
  /**
   * Go to the previous page
   */
  previous: (() => void) | undefined;
  /**
   * Go to a specific page by index
   * @param index - The index of the page to go to
   */
  goTo: (index: number) => void;
  /**
   * Go to a specific page by page number
   * @param page - The page number to go to
   */
  goToPage: (page: number) => void;
  /**
   * The current page index and total number of pages
   * To be used for UI display. Page is not 0-based.
   */
  display: {
    page: number;
    total: number;
  };
};

export const usePageNavigation = <T>({
  pages,
}: PageNavigationProps<T>): PageNavigation<T> => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const maxIndex = Math.max(pages.length - 1, 0); // avoid -1 for empty pages
  const previousPageLength = usePrevious(pages.length, { setInitial: true });
  if (previousPageLength !== pages.length) {
    // make sure the current index is within the new bounds
    setCurrentIndex(clamp(currentIndex, 0, maxIndex));
  }

  const next = useMemo(
    () =>
      currentIndex < maxIndex
        ? () => setCurrentIndex(currentIndex + 1)
        : undefined,
    [currentIndex, maxIndex],
  );

  const previous = useMemo(
    () =>
      currentIndex > 0 ? () => setCurrentIndex(currentIndex - 1) : undefined,
    [currentIndex],
  );

  const goTo = useCallback(
    (index: number) => {
      setCurrentIndex(clamp(index, 0, maxIndex));
    },
    [maxIndex],
  );
  const goToPage = useCallback(
    (page: number) => {
      setCurrentIndex(clamp(page - 1, 0, maxIndex));
    },
    [maxIndex],
  );

  return {
    currentIndex,
    current: pages[currentIndex],
    next,
    previous,
    goTo,
    goToPage,
    display: {
      page: currentIndex + 1,
      total: pages.length,
    },
  };
};
